import { z } from "zod";

export const WithDbId = <T extends z.ZodType>(schema: T) =>
  z.object({
    id: z.string(),
    value: schema,
  });

export const Currency = z.union([
  z.literal("GBP"),
  z.literal("USD"),
  z.literal("EUR"),
  z.literal("JPY"),
  z.literal("AUD"),
  z.literal("MXN"),
  z.literal("CHF"),
  z.literal("DKK"),
  z.literal("SEK"),
  z.literal("NOK"),
  z.literal("CAD"),
  z.literal("BRL"),
  z.literal("ARS"),
  z.literal("NZD"),
  z.literal("HKD"),
  z.literal("TWD"),
  z.literal("KRW"),
  z.literal("INR"),
  z.literal("AED"),
  z.literal("ZAR"),
]);

export type Currency = z.infer<typeof Currency>;

export const BigDecimal = z.string();
export type BigDecimal = z.infer<typeof BigDecimal>;

export const ClientSegment = z.enum([
  "WineProducer",
  "SpiritsDistiller",
  "SpiritsTrader",
  "Distributor",
]);
export type ClientSegment = z.infer<typeof ClientSegment>;

export const FacilitySegment = z.enum(["Producer", "Wholesaler"]);
export type FacilitySegment = z.infer<typeof FacilitySegment>;

export const FixedRate = z.string();
export type FixedRate = z.infer<typeof FixedRate>;

export const VariableRate = z.object({
  base: z.string(),
  reference: z.string(),
});
export type VariableRate = z.infer<typeof VariableRate>;

export const MonthlyFee = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("fixed"),
    value: FixedRate,
  }),
  z.object({
    kind: z.literal("variable"),
    value: VariableRate,
  }),
]);
export type MonthlyFee = z.infer<typeof MonthlyFee>;

export const OrgSettlementCreditLimit = z.object({
  currency: Currency,
  creditLimit: BigDecimal,
});
export type OrgSettlementCreditLimit = z.infer<typeof OrgSettlementCreditLimit>;

/**
 * StringNE enforces that we have a string that is not empty
 */
export const StringNE = z.string().min(1);
export type StringNE = z.infer<typeof StringNE>;

/**
 * StringMD is a markdown text string
 */
export const StringMD = z.string();
export type StringMD = z.infer<typeof StringMD>;

export const Instant = z.number();
export type Instant = z.infer<typeof Instant>;

export const LocalDate = z.string();
export type LocalDate = z.infer<typeof LocalDate>;

export const Incoterms = z.enum([
  "EXW",
  "FCA",
  "CPT",
  "CIP",
  "DAP",
  "DPU",
  "DDP",
  "FAS",
  "FOB",
  "CFR",
  "CIF",
]);
export type Incoterms = z.infer<typeof Incoterms>;

export const Locale = z.enum(["London", "US"]);
export type Locale = z.infer<typeof Locale>;

export const Country = z.enum([
  "AE",
  "AF",
  "AG",
  "AI",
  "AL",
  "AM",
  "AO",
  "AQ",
  "AR",
  "AS",
  "AT",
  "AU",
  "AW",
  "AX",
  "AZ",
  "BA",
  "BB",
  "BD",
  "BE",
  "BF",
  "BG",
  "BH",
  "BI",
  "BJ",
  "BL",
  "BM",
  "BN",
  "BO",
  "BQ",
  "BR",
  "BS",
  "BT",
  "BV",
  "BW",
  "BY",
  "BZ",
  "CA",
  "CC",
  "CD",
  "CF",
  "CG",
  "CH",
  "CI",
  "CK",
  "CL",
  "CM",
  "CN",
  "CO",
  "CR",
  "CU",
  "CV",
  "CW",
  "CX",
  "CY",
  "CZ",
  "DE",
  "DJ",
  "DK",
  "DM",
  "DO",
  "DZ",
  "EC",
  "EE",
  "EG",
  "EH",
  "ER",
  "ES",
  "ET",
  "FI",
  "FJ",
  "FK",
  "FM",
  "FO",
  "FR",
  "GA",
  "GB",
  "GD",
  "GE",
  "GF",
  "GG",
  "GH",
  "GI",
  "GL",
  "GM",
  "GN",
  "GP",
  "GQ",
  "GR",
  "GS",
  "GT",
  "GU",
  "GW",
  "GY",
  "HK",
  "HM",
  "HN",
  "HR",
  "HT",
  "HU",
  "ID",
  "IE",
  "IL",
  "IM",
  "IN",
  "IO",
  "IQ",
  "IR",
  "IS",
  "IT",
  "JE",
  "JM",
  "JO",
  "JP",
  "KE",
  "KG",
  "KH",
  "KI",
  "KM",
  "KN",
  "KP",
  "KR",
  "KW",
  "KY",
  "KZ",
  "LA",
  "LB",
  "LC",
  "LI",
  "LK",
  "LR",
  "LS",
  "LT",
  "LU",
  "LV",
  "LY",
  "MA",
  "MC",
  "MD",
  "ME",
  "MF",
  "MG",
  "MH",
  "MK",
  "ML",
  "MM",
  "MN",
  "MO",
  "MP",
  "MQ",
  "MR",
  "MS",
  "MT",
  "MU",
  "MV",
  "MW",
  "MX",
  "MY",
  "MZ",
  "NA",
  "NC",
  "NE",
  "NF",
  "NG",
  "NI",
  "NL",
  "NO",
  "NP",
  "NR",
  "NU",
  "NZ",
  "OM",
  "PA",
  "PE",
  "PF",
  "PG",
  "PH",
  "PK",
  "PL",
  "PM",
  "PN",
  "PR",
  "PS",
  "PT",
  "PW",
  "PY",
  "QA",
  "RE",
  "RO",
  "RS",
  "RU",
  "RW",
  "SA",
  "SB",
  "SC",
  "SD",
  "SE",
  "SG",
  "SH",
  "SI",
  "SJ",
  "SK",
  "SL",
  "SM",
  "SN",
  "SO",
  "SR",
  "SS",
  "ST",
  "SV",
  "SX",
  "SY",
  "SZ",
  "TC",
  "TD",
  "TF",
  "TG",
  "TH",
  "TJ",
  "TK",
  "TL",
  "TM",
  "TN",
  "TO",
  "TR",
  "TT",
  "TV",
  "TW",
  "TZ",
  "UA",
  "UG",
  "UM",
  "US",
  "UY",
  "UZ",
  "VA",
  "VC",
  "VE",
  "VG",
  "VI",
  "VN",
  "VU",
  "WF",
  "WS",
  "YE",
  "YT",
  "ZA",
  "ZM",
  "ZW",
]);
export type Country = z.infer<typeof Country>;

export const BillingBasis = z.enum(["ordered", "finished"]);
export type BillingBasis = z.infer<typeof BillingBasis>;

export const ContactPerson = z.object({
  name: StringNE,
  email: StringNE.nullable(),
  phoneNumber: StringNE.nullable(),
});
export type ContactPerson = z.infer<typeof ContactPerson>;

export const Address = z.object({
  streetName: StringNE,
  line1: StringNE.nullable(),
  line2: StringNE.nullable(),
  town: StringNE,
  country: Country,
  postCode: StringNE,
  state: StringNE.nullable(),
});
export type Address = z.infer<typeof Address>;
